import { useEffect, useRef, useState } from "react";
import api from "./api";

/**
 * Google IMA SDK preroll integration.
 *
 * Usage:
 *   const { adContainerRef, startPreroll, playedAd } = useVideoPreroll({ videoId });
 *   <div ref={adContainerRef} className="absolute inset-0 z-20" />
 *   useEffect(() => { startPreroll(videoEl); }, [videoEl]);
 *
 * Behaviour:
 *   - Fetches /api/ads/vast for the configured placement.
 *   - If url is null → resolves immediately (no ad providers enabled).
 *   - Otherwise loads IMA, requests ads, plays them in an overlay, then
 *     resolves so the host video can play.
 *   - Logs events to /api/ads/impression for analytics.
 *
 * Props:
 *   videoId   — optional, included in VAST macro so reports are per-video.
 *   placement — 'preroll' | 'midroll' | 'postroll' (default: preroll)
 *   enabled   — skip entirely when false (e.g. members-only locked).
 */
export default function useVideoPreroll({ videoId, placement = "preroll", enabled = true } = {}) {
  const adContainerRef = useRef(null);
  const adDisplayRef = useRef(null);
  const adsLoaderRef = useRef(null);
  const adsManagerRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [played, setPlayed] = useState(false);

  useEffect(() => () => cleanup(), []);

  const cleanup = () => {
    try { adsManagerRef.current?.destroy(); } catch {}
    try { adsLoaderRef.current?.destroy(); } catch {}
    try { adDisplayRef.current?.destroy(); } catch {}
    adsManagerRef.current = null;
    adsLoaderRef.current = null;
    adDisplayRef.current = null;
  };

  const log = (kind, extra = {}) => {
    try {
      api.post("/ads/impression", {
        kind, placement, video_id: videoId, ...extra,
      });
    } catch {}
  };

  /**
   * Load + play the preroll. Returns a Promise that resolves when the ad
   * completes (success OR error / no-fill). Host player should then unhide.
   */
  const startPreroll = (hostVideoEl) => {
    return new Promise(async (resolve) => {
      if (!enabled || !hostVideoEl || !adContainerRef.current) { resolve({ played: false, reason: "disabled" }); return; }
      // IMA may not have loaded yet (async tag). Wait up to 2s.
      const waitForIma = () => new Promise((r) => {
        let tries = 0;
        const check = () => {
          if (window.google?.ima) return r(true);
          if (tries++ > 40) return r(false);
          setTimeout(check, 50);
        };
        check();
      });
      const imaReady = await waitForIma();
      if (!imaReady) { resolve({ played: false, reason: "ima_not_ready" }); return; }

      // Fetch VAST tag URL from backend
      let url = null, provider = null;
      try {
        const { data } = await api.get(`/ads/vast`, { params: { placement, video_id: videoId || "" } });
        url = data.url; provider = data.provider;
      } catch { /* ignore */ }
      if (!url) { resolve({ played: false, reason: "no_provider" }); return; }

      const ima = window.google.ima;
      adDisplayRef.current = new ima.AdDisplayContainer(adContainerRef.current, hostVideoEl);
      adDisplayRef.current.initialize();
      adsLoaderRef.current = new ima.AdsLoader(adDisplayRef.current);

      adsLoaderRef.current.addEventListener(ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED, (e) => {
        try {
          const settings = new ima.AdsRenderingSettings();
          settings.restoreCustomPlaybackStateOnAdBreakComplete = true;
          adsManagerRef.current = e.getAdsManager(hostVideoEl, settings);
          adsManagerRef.current.addEventListener(ima.AdEvent.Type.STARTED, () => { setPlaying(true); log("impression", { provider }); });
          adsManagerRef.current.addEventListener(ima.AdEvent.Type.CLICK, () => log("click", { provider }));
          adsManagerRef.current.addEventListener(ima.AdEvent.Type.SKIPPED, () => log("skip", { provider }));
          adsManagerRef.current.addEventListener(ima.AdEvent.Type.COMPLETE, () => {
            setPlaying(false); setPlayed(true); cleanup(); resolve({ played: true, reason: "complete", provider });
          });
          adsManagerRef.current.addEventListener(ima.AdEvent.Type.ALL_ADS_COMPLETED, () => {
            setPlaying(false); setPlayed(true); cleanup(); resolve({ played: true, reason: "all_complete", provider });
          });
          adsManagerRef.current.addEventListener(ima.AdErrorEvent.Type.AD_ERROR, (err) => {
            log("error", { provider, error_code: err.getError?.()?.getErrorCode?.() });
            setPlaying(false); cleanup(); resolve({ played: false, reason: "ad_error", provider });
          });
          const rect = hostVideoEl.getBoundingClientRect();
          adsManagerRef.current.init(rect.width, rect.height, ima.ViewMode.NORMAL);
          adsManagerRef.current.start();
        } catch (err) {
          cleanup();
          resolve({ played: false, reason: "manager_init_failed" });
        }
      }, false);

      adsLoaderRef.current.addEventListener(ima.AdErrorEvent.Type.AD_ERROR, (err) => {
        log("error", { provider, error_code: err.getError?.()?.getErrorCode?.() });
        cleanup();
        resolve({ played: false, reason: "loader_error", provider });
      }, false);

      const request = new ima.AdsRequest();
      request.adTagUrl = url;
      request.linearAdSlotWidth = hostVideoEl.clientWidth || 640;
      request.linearAdSlotHeight = hostVideoEl.clientHeight || 360;
      request.nonLinearAdSlotWidth = hostVideoEl.clientWidth || 640;
      request.nonLinearAdSlotHeight = Math.floor((hostVideoEl.clientHeight || 360) / 3);
      adsLoaderRef.current.requestAds(request);

      // Hard timeout: 8s for the whole preroll negotiation
      setTimeout(() => {
        if (adsManagerRef.current) return; // already playing
        cleanup();
        resolve({ played: false, reason: "timeout" });
      }, 8000);
    });
  };

  return { adContainerRef, startPreroll, playing, played };
}
